'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useRouter } from 'next/navigation';
import {
  Eye, EyeOff, ArrowRight, Shield, Zap, Globe,
  TrendingUp, Users, Server, Check, UserPlus
} from 'lucide-react';

import { toast } from 'sonner';
import AppLogo from '@/components/ui/AppLogo';
import { useAuth } from '@/contexts/AuthContext';


interface LoginFormData {
  email: string;
  password: string;
  rememberMe: boolean;
}

interface SignUpFormData {
  fullName: string;
  email: string;
  password: string;
  confirmPassword: string;
}


const agencyStats = [
  { id: 'stat-mrr', label: 'Monthly Recurring Revenue', value: '$12,450', icon: TrendingUp, color: 'var(--positive)' },
  { id: 'stat-clients', label: 'Active Clients', value: '23', icon: Users, color: 'var(--primary)' },
  { id: 'stat-sites', label: 'Sites Managed', value: '18', icon: Globe, color: 'var(--accent)' },
  { id: 'stat-uptime', label: 'Avg Uptime', value: '99.94%', icon: Server, color: 'var(--violet)' },
];


export default function LoginContent() {
  const router = useRouter();
  const { signIn, signUp, resetPassword } = useAuth();
  const [activeTab, setActiveTab] = useState<'signin' | 'signup'>('signin');
  const [showPassword, setShowPassword] = useState(false);
  const [showSignUpPassword, setShowSignUpPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isResettingPassword, setIsResettingPassword] = useState(false);
  const [signUpSuccess, setSignUpSuccess] = useState(false);

  const {
    register,
    handleSubmit,
    setValue,
    getValues,
    formState: { errors },
  } = useForm<LoginFormData>({
    defaultValues: { rememberMe: false },
  });

  const {
    register: registerSignUp,
    handleSubmit: handleSignUpSubmit,
    watch: watchSignUp,
    formState: { errors: signUpErrors },
  } = useForm<SignUpFormData>();

  const signUpPassword = watchSignUp('password');

  const handleForgotPassword = async () => {
    const email = getValues('email');
    if (!email) {
      toast.error('Enter your email address first, then click Forgot password');
      return;
    }
    setIsResettingPassword(true);
    try {
      await resetPassword(email);
      toast.success('Password reset email sent — check your inbox');
    } catch (err: any) {
      toast.error(err?.message || 'Failed to send reset email');
    } finally {
      setIsResettingPassword(false);
    }
  };

  const onSubmit = async (data: LoginFormData) => {
    setIsLoading(true);
    try {
      await signIn(data.email, data.password);
      toast.success('Welcome back!');
      router.replace('/dashboard');
    } catch (err: any) {
      const msg = err?.message || 'Invalid credentials';
      if (msg.toLowerCase().includes('invalid login')) {
        toast.error('Invalid email or password');
      } else if (msg.toLowerCase().includes('email not confirmed')) {
        toast.error('Please verify your email before signing in');
      } else {
        toast.error(msg);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const onSignUp = async (data: SignUpFormData) => {
    setIsLoading(true);
    try {
      await signUp(data.email, data.password, { fullName: data.fullName });
      // Show email verification message — do NOT redirect immediately.
      // The auth/callback route will handle routing to /create-organization
      // after the user clicks the verification link in their email.
      setSignUpSuccess(true);
      toast.success('Account created! Please verify your email before continuing.');
    } catch (err: any) {
      const msg = err?.message || 'Failed to create account';
      if (msg.toLowerCase().includes('already registered')) {
        toast.error('An account with this email already exists. Please sign in.');
        setActiveTab('signin');
      } else {
        toast.error(msg);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-background">
      {/* Left Brand Panel */}
      <div className="hidden lg:flex lg:w-[52%] xl:w-[55%] flex-col relative overflow-hidden auth-gradient">
        <div className="absolute inset-0 grid-bg opacity-60" />
        <div className="absolute top-1/4 left-1/3 w-72 h-72 rounded-full opacity-10" style={{ background: 'radial-gradient(circle, var(--primary) 0%, transparent 70%)', filter: 'blur(40px)' }} />
        <div className="absolute bottom-1/4 right-1/4 w-48 h-48 rounded-full opacity-8" style={{ background: 'radial-gradient(circle, var(--accent) 0%, transparent 70%)', filter: 'blur(30px)' }} />

        <div className="relative z-10 flex flex-col h-full px-12 py-10">
          <div className="flex items-center gap-3">
            <AppLogo size={36} />
            <div>
              <span className="font-bold text-lg text-foreground tracking-tight">TrueLight</span>
              <span className="block text-muted-foreground font-medium tracking-widest uppercase" style={{ fontSize: '9px' }}>Operating System</span>
            </div>
          </div>

          <div className="flex-1 flex flex-col justify-center max-w-md">
            <div className="mb-8">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-primary/30 bg-primary/10 mb-5">
                <Zap size={12} className="text-primary" />
                <span className="text-xs font-semibold text-primary">Agency Command Center</span>
              </div>
              <h1 className="text-4xl font-bold text-foreground leading-tight mb-4">
                Your agency.<br />
                <span className="text-gradient-blue">Fully in control.</span>
              </h1>
              <p className="text-base text-muted-foreground leading-relaxed">
                TrueLight OS brings your leads, clients, projects, billing, and hosting into one premium dashboard — built for how you actually work.
              </p>
            </div>

            <div className="space-y-3 mb-10">
              {[
                { icon: Shield, text: 'Role-based access with 2FA ready' },
                { icon: TrendingUp, text: 'Real-time revenue and MRR tracking' },
                { icon: Globe, text: 'Live uptime monitoring for all client sites' },
                { icon: Users, text: 'Full CRM pipeline from lead to signed contract' },
              ].map((feat) => {
                const FeatIcon = feat.icon;
                return (
                  <div key={`feat-${feat.text.slice(0, 20)}`} className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(29,110,232,0.12)' }}>
                      <FeatIcon size={13} className="text-primary" />
                    </div>
                    <span className="text-sm text-muted-foreground">{feat.text}</span>
                  </div>
                );
              })}
            </div>

            <div className="grid grid-cols-2 gap-3">
              {agencyStats.map((stat) => {
                const StatIcon = stat.icon;
                return (
                  <div key={stat.id} className="p-3.5 rounded-xl border border-border glass-card">
                    <div className="flex items-center gap-2 mb-1.5">
                      <StatIcon size={13} style={{ color: stat.color }} />
                      <p className="text-muted-foreground" style={{ fontSize: '10px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{stat.label}</p>
                    </div>
                    <p className="text-xl font-bold text-foreground metric-value">{stat.value}</p>
                  </div>
                );
              })}
            </div>
          </div>

          <p className="text-xs text-muted-foreground">
            © 2026 TrueLight Web Agency. All rights reserved.
          </p>
        </div>
      </div>

      {/* Right Form Panel */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 sm:px-10 py-12 relative">
        <div className="lg:hidden flex items-center gap-3 mb-10">
          <AppLogo size={36} />
          <div>
            <span className="font-bold text-lg text-foreground">TrueLight OS</span>
          </div>
        </div>

        <div className="w-full max-w-[400px]">
          {/* Tab Switcher */}
          <div className="flex rounded-xl border border-border p-1 mb-8" style={{ background: 'rgba(255,255,255,0.03)' }}>
            <button
              type="button"
              onClick={() => setActiveTab('signin')}
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-semibold transition-all ${
                activeTab === 'signin' ?'bg-primary text-white shadow-sm' :'text-muted-foreground hover:text-foreground'
              }`}
            >
              Sign In
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('signup')}
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-semibold transition-all ${
                activeTab === 'signup' ?'bg-primary text-white shadow-sm' :'text-muted-foreground hover:text-foreground'
              }`}
            >
              <UserPlus size={14} />
              Create Account
            </button>
          </div>

          {/* ── SIGN IN FORM ── */}
          {activeTab === 'signin' && (
            <>
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-foreground mb-2">Sign in to TrueLight OS</h2>
                <p className="text-sm text-muted-foreground">Enter your credentials to access the agency dashboard.</p>
              </div>

              <form onSubmit={handleSubmit(onSubmit)} className="space-y-5" noValidate>
                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-foreground mb-1.5">
                    Email address
                  </label>
                  <input
                    id="email"
                    type="email"
                    autoComplete="email"
                    placeholder="you@truelightweb.com"
                    className={`input-field w-full px-3.5 py-2.5 text-sm ${errors.email ? 'border-negative focus:border-negative focus:shadow-none' : ''}`}
                    {...register('email', {
                      required: 'Email is required',
                      pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email address' },
                    })}
                  />
                  {errors.email && (
                    <p className="text-xs text-negative mt-1.5 flex items-center gap-1">
                      <span>⚠</span> {errors.email.message}
                    </p>
                  )}
                </div>

                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label htmlFor="password" className="text-sm font-semibold text-foreground">
                      Password
                    </label>
                    <button
                      type="button"
                      onClick={handleForgotPassword}
                      disabled={isResettingPassword}
                      className="text-xs text-primary hover:text-accent transition-colors font-medium disabled:opacity-60"
                    >
                      {isResettingPassword ? 'Sending...' : 'Forgot password?'}
                    </button>
                  </div>
                  <div className="relative">
                    <input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      autoComplete="current-password"
                      placeholder="Enter your password"
                      className={`input-field w-full px-3.5 py-2.5 pr-11 text-sm ${errors.password ? 'border-negative focus:border-negative focus:shadow-none' : ''}`}
                      {...register('password', {
                        required: 'Password is required',
                        minLength: { value: 6, message: 'Password must be at least 6 characters' },
                      })}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                  {errors.password && (
                    <p className="text-xs text-negative mt-1.5 flex items-center gap-1">
                      <span>⚠</span> {errors.password.message}
                    </p>
                  )}
                </div>

                <div className="flex items-center gap-2.5">
                  <input
                    id="rememberMe"
                    type="checkbox"
                    className="w-4 h-4 rounded border-border accent-primary cursor-pointer"
                    {...register('rememberMe')}
                  />
                  <label htmlFor="rememberMe" className="text-sm text-muted-foreground cursor-pointer">
                    Keep me signed in for 30 days
                  </label>
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  className="btn-primary w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-semibold text-sm disabled:opacity-60 disabled:cursor-not-allowed disabled:transform-none"
                  style={{ minHeight: '44px' }}
                >
                  {isLoading ? (
                    <>
                      <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                      Signing in...
                    </>
                  ) : (
                    <>
                      Sign in to TrueLight OS
                      <ArrowRight size={16} />
                    </>
                  )}
                </button>
              </form>

              <div className="flex items-center gap-2 mt-4 px-3.5 py-2.5 rounded-xl border border-border" style={{ background: 'rgba(29,110,232,0.05)' }}>
                <Shield size={13} className="text-primary flex-shrink-0" />
                <p className="text-xs text-muted-foreground">Two-factor authentication is available in account settings for enhanced security.</p>
              </div>

              <p className="text-center text-xs text-muted-foreground mt-6">
                Don&apos;t have an account?{' '}
                <button type="button" onClick={() => setActiveTab('signup')} className="text-primary hover:underline font-medium">
                  Create one free
                </button>
              </p>
            </>
          )}

          {/* ── SIGN UP FORM ── */}
          {activeTab === 'signup' && (
            <>
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-foreground mb-2">Create your account</h2>
                <p className="text-sm text-muted-foreground">Start your TrueLight OS journey. No credit card required to sign up.</p>
              </div>

              {signUpSuccess ? (
                <div className="rounded-xl border border-positive/30 bg-positive/10 p-6 text-center">
                  <div className="w-12 h-12 rounded-full bg-positive/20 flex items-center justify-center mx-auto mb-4">
                    <Check size={24} className="text-positive" />
                  </div>
                  <h3 className="font-bold text-foreground mb-2">Check your email</h3>
                  <p className="text-sm text-muted-foreground">
                    We&apos;ve sent a verification link to your email address. Please verify your email before continuing.
                  </p>
                  <p className="text-xs text-muted-foreground mt-3">
                    After clicking the link in your email, you&apos;ll be taken to set up your organization.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSignUpSubmit(onSignUp)} className="space-y-5" noValidate>
                  <div>
                    <label htmlFor="fullName" className="block text-sm font-semibold text-foreground mb-1.5">
                      Full name
                    </label>
                    <input
                      id="fullName"
                      type="text"
                      autoComplete="name"
                      placeholder="Jane Smith"
                      className={`input-field w-full px-3.5 py-2.5 text-sm ${signUpErrors.fullName ? 'border-negative' : ''}`}
                      {...registerSignUp('fullName', {
                        required: 'Full name is required',
                        minLength: { value: 2, message: 'Name must be at least 2 characters' },
                      })}
                    />
                    {signUpErrors.fullName && (
                      <p className="text-xs text-negative mt-1.5 flex items-center gap-1">
                        <span>⚠</span> {signUpErrors.fullName.message}
                      </p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="signupEmail" className="block text-sm font-semibold text-foreground mb-1.5">
                      Email address
                    </label>
                    <input
                      id="signupEmail"
                      type="email"
                      autoComplete="email"
                      placeholder="you@youragency.com"
                      className={`input-field w-full px-3.5 py-2.5 text-sm ${signUpErrors.email ? 'border-negative' : ''}`}
                      {...registerSignUp('email', {
                        required: 'Email is required',
                        pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email address' },
                      })}
                    />
                    {signUpErrors.email && (
                      <p className="text-xs text-negative mt-1.5 flex items-center gap-1">
                        <span>⚠</span> {signUpErrors.email.message}
                      </p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="signupPassword" className="block text-sm font-semibold text-foreground mb-1.5">
                      Password
                    </label>
                    <div className="relative">
                      <input
                        id="signupPassword"
                        type={showSignUpPassword ? 'text' : 'password'}
                        autoComplete="new-password"
                        placeholder="At least 8 characters"
                        className={`input-field w-full px-3.5 py-2.5 pr-11 text-sm ${signUpErrors.password ? 'border-negative' : ''}`}
                        {...registerSignUp('password', {
                          required: 'Password is required',
                          minLength: { value: 8, message: 'Password must be at least 8 characters' },
                        })}
                      />
                      <button
                        type="button"
                        onClick={() => setShowSignUpPassword(!showSignUpPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                        aria-label={showSignUpPassword ? 'Hide password' : 'Show password'}
                      >
                        {showSignUpPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                    {signUpErrors.password && (
                      <p className="text-xs text-negative mt-1.5 flex items-center gap-1">
                        <span>⚠</span> {signUpErrors.password.message}
                      </p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="confirmPassword" className="block text-sm font-semibold text-foreground mb-1.5">
                      Confirm password
                    </label>
                    <div className="relative">
                      <input
                        id="confirmPassword"
                        type={showConfirmPassword ? 'text' : 'password'}
                        autoComplete="new-password"
                        placeholder="Repeat your password"
                        className={`input-field w-full px-3.5 py-2.5 pr-11 text-sm ${signUpErrors.confirmPassword ? 'border-negative' : ''}`}
                        {...registerSignUp('confirmPassword', {
                          required: 'Please confirm your password',
                          validate: (val) => val === signUpPassword || 'Passwords do not match',
                        })}
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                        aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                      >
                        {showConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                    {signUpErrors.confirmPassword && (
                      <p className="text-xs text-negative mt-1.5 flex items-center gap-1">
                        <span>⚠</span> {signUpErrors.confirmPassword.message}
                      </p>
                    )}
                  </div>

                  <button
                    type="submit"
                    disabled={isLoading}
                    className="btn-primary w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-semibold text-sm disabled:opacity-60 disabled:cursor-not-allowed disabled:transform-none"
                    style={{ minHeight: '44px' }}
                  >
                    {isLoading ? (
                      <>
                        <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                        </svg>
                        Creating account...
                      </>
                    ) : (
                      <>
                        Create my account
                        <ArrowRight size={16} />
                      </>
                    )}
                  </button>

                  <p className="text-xs text-muted-foreground text-center">
                    By creating an account you agree to our{' '}
                    <span className="text-primary">Terms of Service</span> and{' '}
                    <span className="text-primary">Privacy Policy</span>.
                  </p>
                </form>
              )}

              <p className="text-center text-xs text-muted-foreground mt-6">
                Already have an account?{' '}
                <button type="button" onClick={() => setActiveTab('signin')} className="text-primary hover:underline font-medium">
                  Sign in
                </button>
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}